@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.engineservice.cr

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardNode
import org.example.project.engineletter.RenderCrLetterSys
import org.example.project.engineletter.layouts.buildCRLayoutSysOwnDataLine
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.engineservice.ServiceApi
import org.example.project.enginetabular.currentDateDE
import org.example.project.manager.NodeScope
import org.example.project.screens_sys.UnternehmenstammApi
import org.example.project.service.CRChatPopupService
import org.example.project.service.CustomerApi
import org.example.project.service.GlobalManager
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.LogoSettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

private const val SYS_CR_PDF_PREVIEW_ROOT_ID = "sys-cr-pdf-preview-root"
private const val SYS_CR_SIGNATORY_NAME = "Tim Stolz"
private const val SYS_CR_SIGNATORY_ROLE = "Geschaeftsfuehrer, lumatica"

private fun estimatedEffortText(ticket: All): String =
    ticket.crAufwandStunden.takeIf { it > 0 }?.let { "Aufwand geschaetzt: $it Stunden" }.orEmpty()

private fun offerCreatedOrtDatumText(unternehmen: All): String =
    listOf(
        "Angebot erstellt",
        listOf(unternehmen.ort, currentDateDE()).filter { it.isNotBlank() }.joinToString(", ")
    ).filter { it.isNotBlank() }.joinToString("\n")

@Composable
fun SysCRScreen() {
    val scope = rememberCoroutineScope()
    val crLiveVersion = GlobalManager.jsonDomainVersion("cr")
    val internalTitleVisible = LocalScreenInternalTitleVisible.current

    var crTickets by remember { mutableStateOf<List<All>>(emptyList()) }
    var crFilter by remember { mutableStateOf("ALLE") }
    var selectedCrId by remember { mutableStateOf<String?>(null) }
    var showPdfPreview by remember { mutableStateOf(false) }
    var editedAnforderung by remember { mutableStateOf("") }
    var editedAufwandStunden by remember { mutableStateOf("") }
    var editedAufwandFinal by remember { mutableStateOf("") }
    var editedKommentarIntern by remember { mutableStateOf("") }
    var editedCrInhalt by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    var defaultLogoSrc by remember { mutableStateOf("") }
    var unternehmen by remember { mutableStateOf(All()) }
    var currentSysUser by remember { mutableStateOf(All()) }
    var selectedCrPdfUrl by remember { mutableStateOf("") }
    var selectedKunde by remember { mutableStateOf<All?>(null) }

    val overlayArt = if (GlobalManager.currentFirmenId.isBlank()) NodeScope.SYS else NodeScope.CUS
    val overlayNode = when (overlayArt) {
        NodeScope.CUS -> DashboardNode.CUS
        NodeScope.NET -> DashboardNode.NET
        else -> DashboardNode.SYS
    }

    suspend fun reload() {
        crTickets = ServiceApi.loadCr("all").sortedByDescending { it.id }
    }

    suspend fun openCrPdf(ticket: All): Boolean {
        val fileName = ticket.crAngebotPdf.ifBlank { ServiceApi.crPdfFileName(ticket) }
        return ServiceApi.openCrDocument(ticket.id, fileName)
    }

    LaunchedEffect(crLiveVersion) {
        reload()
        defaultLogoSrc = LogoSettings.changeRequestDefaultLogo
        currentSysUser = LoginService.getUser() ?: All()
        unternehmen = runCatching { UnternehmenstammApi.loadUnternehmen() ?: All() }.getOrDefault(All())
    }

    val selectedCr = crTickets.firstOrNull { it.id == selectedCrId }
    val offeneTickets = crTickets.count { !ZustandManager.CR.isFinal(it.zustand) }
    val abgeschlosseneTickets = crTickets.count { ZustandManager.CR.isFinal(it.zustand) }
    val filteredCrTickets = when (crFilter) {
        "OFFEN" -> crTickets.filter { !ZustandManager.CR.isFinal(it.zustand) }
        "CLOSE" -> crTickets.filter { ZustandManager.CR.isFinal(it.zustand) }
        else -> crTickets
    }

    fun openCr(ticket: All) {
        scope.launch {
            if (ticket.zustand == ZustandManager.CR.EINGESPIELT) {
                openCrPdf(ticket)
                return@launch
            }
            editedAnforderung = ticket.crAnforderung
            editedAufwandStunden = ticket.crAufwandStunden.takeIf { it > 0 }?.toString().orEmpty()
            editedAufwandFinal = ticket.crAufwandFinal.takeIf { it > 0 }?.toString().orEmpty()
            editedKommentarIntern = ticket.crKommentarIntern
            editedCrInhalt = ticket.crInhalt
            selectedCrPdfUrl = if (ticket.crAngebotPdf.isNotBlank()) "available" else ""
            selectedKunde = ticket.firmenId.takeIf { it.isNotBlank() }?.let { CustomerApi.loadDetails(it) }
            selectedCrId = ticket.id
            showPdfPreview = false
        }
    }

    fun currentCrPreviewText(ticket: All): String =
        editedCrInhalt.ifBlank { ticket.crInhalt }.ifBlank { "-" }

    fun buildCrPreviewAddress(strasse: String, hausnummer: String, plz: String, ort: String, land: String = ""): String {
        val zeile1 = listOf(strasse, hausnummer).filter { it.isNotBlank() }.joinToString(" ")
        val zeile2 = listOf(plz, ort).filter { it.isNotBlank() }.joinToString(" ")
        return listOf(zeile1, zeile2, land).filter { it.isNotBlank() }.joinToString("\n")
    }

    fun lineAt(value: String, index: Int): String =
        value.replace("\r\n", "\n").split("\n").getOrNull(index).orEmpty()

    fun buildCrPreviewLayout(ticket: All, kunde: All?): CrPreviewLayout {
        val empfaengerBlock = if (kunde != null) {
            val kopf = kunde.firma.ifBlank { kunde.ansprechpartner }
            val adresse = buildCrPreviewAddress(kunde.strasse, kunde.hausnummer, kunde.plz, kunde.ort)
            listOfNotNull(kopf.takeIf { it.isNotBlank() }, adresse.takeIf { it.isNotBlank() }).joinToString("\n")
        } else {
            val fallbackAdresse = buildCrPreviewAddress(ticket.strasse, ticket.hausnummer, ticket.plz, ticket.ort)
            val fallbackName = ticket.firma.ifBlank { "-" }
            listOfNotNull(fallbackName.takeIf { it.isNotBlank() }, fallbackAdresse.takeIf { it.isNotBlank() }).joinToString("\n")
        }
        val absenderAdresse = buildCrPreviewAddress(
            strasse = unternehmen.strasse,
            hausnummer = unternehmen.hausnummer,
            plz = unternehmen.plz,
            ort = unternehmen.ort,
            land = unternehmen.land
        )
        val datumText = listOf(unternehmen.ort, currentDateDE()).filter { it.isNotBlank() }.joinToString(", ").ifBlank { currentDateDE() }
        return CrPreviewLayout(
            eigeneDatenZeile = buildCRLayoutSysOwnDataLine(unternehmen.firma, absenderAdresse),
            empfaengerFirmenname = lineAt(empfaengerBlock, 0),
            empfaengerStrasseHausnummer = lineAt(empfaengerBlock, 1),
            empfaengerPlzOrt = lineAt(empfaengerBlock, 2),
            firmenIdText = estimatedEffortText(ticket),
            absenderFirmenName = unternehmen.firma,
            absenderStrasseHausnummer = lineAt(absenderAdresse, 0),
            absenderPlzOrt = lineAt(absenderAdresse, 1),
            absenderDatum = datumText,
            betreffText = ticket.crNummer.ifBlank { "CR-${ticket.id}" },
            briefText = currentCrPreviewText(ticket),
            ortDatumText = offerCreatedOrtDatumText(unternehmen),
            grussformelText = "",
            signaturBildSrc = currentSysUser.signatur,
            signaturNameText = listOf(SYS_CR_SIGNATORY_NAME, SYS_CR_SIGNATORY_ROLE).joinToString("\n"),
            logoSrc = defaultLogoSrc,
            footerLines = listOf(
                buildCRLayoutSysOwnDataLine(unternehmen.firma, absenderAdresse),
                listOf(unternehmen.webseite, unternehmen.email, unternehmen.telefon, unternehmen.fax).filter { it.isNotBlank() }.joinToString(" | "),
                listOf(unternehmen.kreditinstitut, unternehmen.iban, unternehmen.umsatzsteuerId).filter { it.isNotBlank() }.joinToString(" | ")
            )
        )
    }

    suspend fun exportCrPdf(ticket: All): Boolean {
        val root = document.getElementById(SYS_CR_PDF_PREVIEW_ROOT_ID) as? HTMLElement ?: return false
        return runCatching {
            val fileName = ticket.crAngebotPdf.ifBlank { ServiceApi.crPdfFileName(ticket) }
            val pdfFile = renderLetterDocumentPdfFile(root, fileName)
            selectedCrPdfUrl = ServiceApi.uploadServiceDocument("cr", ticket.id, pdfFile, fileName)
            selectedCrPdfUrl.isNotBlank()
        }.getOrDefault(false)
    }

    suspend fun sendOffer() {
        val ticket = selectedCr ?: return
        val aufwand = editedAufwandStunden.trim().toIntOrNull() ?: 0
        val aufwandFinal = editedAufwandFinal.trim().toIntOrNull() ?: 0
        if (editedCrInhalt.isBlank() || aufwand <= 0) return

        val fileName = ServiceApi.crPdfFileName(ticket)
        val updated = ServiceApi.sendCrOffer(
            crId = ticket.id,
            crAufwandStunden = aufwand,
            crAufwandFinal = aufwandFinal,
            crKommentarIntern = editedKommentarIntern,
            crInhalt = editedCrInhalt,
            angebotPdf = fileName
        ) ?: return

        ServiceApi.saveCrPreview(
            crId = updated.id,
            preview = buildCrPreviewLayout(
                ticket = updated.copy(
                    crAufwandStunden = aufwand,
                    crAufwandFinal = aufwandFinal,
                    crKommentarIntern = editedKommentarIntern,
                    crInhalt = editedCrInhalt
                ),
                kunde = selectedKunde
            )
        )

        val pdfSaved = exportCrPdf(updated)
        reload()
        selectedCrId = updated.id
        editedAnforderung = updated.crAnforderung
        editedAufwandStunden = updated.crAufwandStunden.takeIf { it > 0 }?.toString().orEmpty()
        editedAufwandFinal = updated.crAufwandFinal.takeIf { it > 0 }?.toString().orEmpty()
        editedKommentarIntern = updated.crKommentarIntern
        editedCrInhalt = updated.crInhalt
        showPdfPreview = false
        if (!pdfSaved) {
            AppDialogs.alert("Angebot wurde gespeichert, aber das CR-PDF konnte nicht erstellt werden.")
        }
    }

    fun workflowActionLabel(ticket: All): String = when (ticket.zustand) {
        ZustandManager.CR.VERBINDLICH -> "Umsetzung starten"
        ZustandManager.CR.IN_PROGRAMMIERUNG -> "Programmierung fertig melden"
        ZustandManager.CR.BEREIT_ZUR_EINSPIELUNG -> "Einspielung bestätigen"
        ZustandManager.CR.IN_TEST -> "Einspielung bestaetigen"
        else -> ""
    }

    fun workflowActionEnabled(ticket: All): Boolean = when (ticket.zustand) {
        ZustandManager.CR.VERBINDLICH -> true
        ZustandManager.CR.IN_PROGRAMMIERUNG -> (editedAufwandFinal.trim().toIntOrNull() ?: 0) > 0
        ZustandManager.CR.BEREIT_ZUR_EINSPIELUNG -> true
        else -> false
    }

    fun workflowTargetState(ticket: All): Int? = when (ticket.zustand) {
        ZustandManager.CR.VERBINDLICH -> ZustandManager.CR.IN_PROGRAMMIERUNG
        ZustandManager.CR.IN_PROGRAMMIERUNG -> ZustandManager.CR.IN_TEST
        ZustandManager.CR.BEREIT_ZUR_EINSPIELUNG -> ZustandManager.CR.EINGESPIELT
        else -> null
    }

    SysCRLayout(
        internalTitleVisible = internalTitleVisible,
        hasSelection = selectedCr != null,
        onBack = {
            showPdfPreview = false
            selectedCrId = null
        },
        headerContent = {},
        content = {
            SysCRBearbeitenLayout(
                crTickets = crTickets,
                crFilter = crFilter,
                filteredCrTickets = filteredCrTickets,
                offeneTickets = offeneTickets,
                abgeschlosseneTickets = abgeschlosseneTickets,
                selectedCr = selectedCr,
                showPdfPreview = showPdfPreview,
                selectedCrPdfUrl = selectedCrPdfUrl,
                editedAnforderung = editedAnforderung,
                editedAufwandStunden = editedAufwandStunden,
                editedAufwandFinal = editedAufwandFinal,
                editedKommentarIntern = editedKommentarIntern,
                editedCrInhalt = editedCrInhalt,
                saving = saving,
                pdfPreviewContent = {
                    if (selectedCr != null) {
                        SysCrPdfPreview(
                            ticket = selectedCr.copy(
                                crAnforderung = editedAnforderung,
                                crAufwandStunden = editedAufwandStunden.trim().toIntOrNull() ?: selectedCr.crAufwandStunden,
                                crAufwandFinal = editedAufwandFinal.trim().toIntOrNull() ?: selectedCr.crAufwandFinal,
                                crKommentarIntern = editedKommentarIntern,
                                crInhalt = editedCrInhalt
                            ),
                            logoSrc = defaultLogoSrc,
                            signaturBildSrc = currentSysUser.signatur,
                            signaturNameText = listOf(SYS_CR_SIGNATORY_NAME, SYS_CR_SIGNATORY_ROLE).joinToString("\n"),
                            unternehmen = unternehmen,
                            kunde = selectedKunde,
                            rootElementId = null,
                            briefText = currentCrPreviewText(selectedCr),
                            grussformelText = "",
                            ortDatumText = offerCreatedOrtDatumText(unternehmen)
                        )
                    }
                },
                onFilterChange = { crFilter = it },
                onOpenCr = ::openCr,
                onEditedAufwandStundenChange = { editedAufwandStunden = it },
                onEditedAufwandFinalChange = { editedAufwandFinal = it },
                onEditedKommentarInternChange = { editedKommentarIntern = it },
                onEditedCrInhaltChange = { editedCrInhalt = it },
                onShowPdfPreviewChange = { showPdfPreview = it },
                onOpenPreview = {
                    val ticket = selectedCr ?: return@SysCRBearbeitenLayout
                    scope.launch {
                        if (ticket.zustand >= ZustandManager.CR.VERBINDLICH) {
                            openCrPdf(ticket)
                        } else {
                            showPdfPreview = true
                        }
                    }
                },
                onOpenChat = { cr -> CRChatPopupService.openPopup(overlayArt, overlayNode, cr) },
                workflowActionLabel = selectedCr?.let(::workflowActionLabel).orEmpty(),
                workflowActionEnabled = selectedCr?.let(::workflowActionEnabled) == true,
                onTriggerWorkflowAction = {
                    val ticket = selectedCr ?: return@SysCRBearbeitenLayout
                    if (!workflowActionEnabled(ticket)) return@SysCRBearbeitenLayout
                    val nextState = workflowTargetState(ticket) ?: return@SysCRBearbeitenLayout
                    saving = true
                    scope.launch {
                        try {
                            val updated = ServiceApi.transitionCrState(
                                crId = ticket.id,
                                nextState = nextState,
                                crAufwandFinal = editedAufwandFinal.trim().toIntOrNull()
                            )
                            if (updated != null) {
                                reload()
                                selectedCrId = updated.id
                                selectedCrPdfUrl = if (updated.crAngebotPdf.isNotBlank()) "available" else ""
                            }
                        } finally {
                            saving = false
                        }
                    }
                },
                onSendOffer = {
                    saving = true
                    scope.launch {
                        try {
                            sendOffer()
                        } finally {
                            saving = false
                        }
                    }
                }
            )
        }
    )

    if (selectedCr != null) {
        Div({
            style {
                position(Position.Fixed)
                left((-10000).px)
                top(0.px)
                width(794.px)
                property("pointer-events", "none")
                opacity(0)
                property("z-index", "-1")
            }
        }) {
            SysCrPdfPreview(
                ticket = selectedCr.copy(
                    crAnforderung = editedAnforderung,
                    crAufwandStunden = editedAufwandStunden.trim().toIntOrNull() ?: selectedCr.crAufwandStunden,
                    crAufwandFinal = editedAufwandFinal.trim().toIntOrNull() ?: selectedCr.crAufwandFinal,
                    crKommentarIntern = editedKommentarIntern,
                    crInhalt = editedCrInhalt
                ),
                logoSrc = defaultLogoSrc,
                signaturBildSrc = currentSysUser.signatur,
                signaturNameText = listOf(SYS_CR_SIGNATORY_NAME, SYS_CR_SIGNATORY_ROLE).joinToString("\n"),
                unternehmen = unternehmen,
                kunde = selectedKunde,
                rootElementId = SYS_CR_PDF_PREVIEW_ROOT_ID,
                briefText = currentCrPreviewText(selectedCr),
                grussformelText = "",
                ortDatumText = offerCreatedOrtDatumText(unternehmen)
            )
        }
    }
}

@Composable
private fun SysCrPdfPreview(
    ticket: All,
    logoSrc: String,
    signaturBildSrc: String,
    signaturNameText: String,
    unternehmen: All,
    kunde: All?,
    rootElementId: String?,
    briefText: String,
    grussformelText: String,
    ortDatumText: String
) {
    RenderCrLetterSys(
        rootElementId = rootElementId,
        ticket = ticket,
        kunde = kunde,
        unternehmen = unternehmen,
        logoSrc = logoSrc,
        briefText = briefText,
        betreffText = ticket.crNummer.ifBlank { "CR-${ticket.id}" },
        firmenIdText = estimatedEffortText(ticket),
        ortDatumText = ortDatumText,
        signaturBildSrc = signaturBildSrc,
        signaturNameText = signaturNameText,
        grussformelText = grussformelText
    )
}
